import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { MessageCircle, Recycle, Share2, PackageX } from "lucide-react";
import { formatIDR, waOrderLink, formatPhone } from "../lib/api";
import { Carousel, CarouselContent, CarouselItem, CarouselPrevious, CarouselNext } from "@/components/ui/carousel";
import ShareDialog from "./ShareDialog";

export default function ProductDialog({ product, open, onOpenChange }) {
    const [shareOpen, setShareOpen] = useState(false);
    const [aktif, setAktif] = useState(0);
    const [carouselApi, setCarouselApi] = useState(null);

    const fotos = product ? (product.images?.length ? product.images : product.image ? [product.image] : []) : [];

    useEffect(() => {
        setAktif(0);
    }, [product?.id]);

    useEffect(() => {
        if (!carouselApi) return;
        const onSelect = () => setAktif(carouselApi.selectedScrollSnap());
        carouselApi.on("select", onSelect);
        return () => carouselApi.off("select", onSelect);
    }, [carouselApi]);

    if (!product) return null;

    return (
        <>
            <Dialog open={open} onOpenChange={onOpenChange}>
                <DialogContent
                    data-testid={`product-detail-modal-${product.id}`}
                    className="max-w-2xl rounded-[26px] border-2 border-brand-ink bg-white p-0 overflow-hidden gap-0"
                >
                    <div className="grid sm:grid-cols-2">
                        <div className="flex flex-col">
                            <div className="relative h-64 sm:h-72 overflow-hidden bg-brand-cream">
                                <Carousel key={product.id} setApi={setCarouselApi} opts={{ loop: fotos.length > 1 }} className="h-full">
                                    <CarouselContent className="h-full ml-0">
                                        {fotos.map((f, i) => (
                                            <CarouselItem key={i} className="h-full pl-0">
                                                <img
                                                    src={f}
                                                    alt={`${product.title} — foto ${i + 1}`}
                                                    className={`w-full h-full object-cover ${product.sold_out ? "opacity-70 saturate-[0.6]" : ""}`}
                                                />
                                            </CarouselItem>
                                        ))}
                                    </CarouselContent>
                                    {fotos.length > 1 && (
                                        <>
                                            <CarouselPrevious
                                                data-testid={`product-gallery-prev-${product.id}`}
                                                className="left-3 top-1/2 -translate-y-1/2 h-9 w-9 rounded-full border-2 border-brand-ink bg-white/95 hover:bg-white shadow-hard-sm"
                                            />
                                            <CarouselNext
                                                data-testid={`product-gallery-next-${product.id}`}
                                                className="right-3 top-1/2 -translate-y-1/2 h-9 w-9 rounded-full border-2 border-brand-ink bg-white/95 hover:bg-white shadow-hard-sm"
                                            />
                                            <span
                                                data-testid={`product-gallery-counter-${product.id}`}
                                                className="absolute bottom-3 right-3 px-2.5 py-1 rounded-full border-2 border-brand-ink bg-white/90 text-[11px] font-extrabold pointer-events-none"
                                            >
                                                {aktif + 1}/{fotos.length}
                                            </span>
                                        </>
                                    )}
                                </Carousel>
                            </div>
                            {fotos.length > 1 && (
                                <div className="flex gap-2 p-3 overflow-x-auto">
                                    {fotos.map((f, i) => (
                                        <button
                                            key={i}
                                            data-testid={`product-thumb-${product.id}-${i}`}
                                            onClick={() => carouselApi?.scrollTo(i)}
                                            aria-label={`Lihat foto ${i + 1}`}
                                            className={`w-14 h-14 rounded-xl border-2 overflow-hidden shrink-0 transition-all ${
                                                i === aktif
                                                    ? "border-brand-forest ring-2 ring-brand-forest/30 opacity-100"
                                                    : "border-brand-ink/25 opacity-60 hover:opacity-100 hover:border-brand-ink/60"
                                            }`}
                                        >
                                            <img src={f} alt="" className="w-full h-full object-cover" />
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                        <div className="p-6 flex flex-col">
                            <DialogHeader>
                                <span className="self-start px-3 py-1 rounded-full border-2 border-brand-ink bg-white text-[11px] font-extrabold">{product.category}</span>
                                <DialogTitle className="font-display text-2xl font-bold mt-3 text-left">{product.title}</DialogTitle>
                                <DialogDescription className="text-sm text-brand-ink/70 leading-relaxed text-left mt-2">
                                    {product.description || "Karya handmade dari bahan daur ulang."}
                                </DialogDescription>
                            </DialogHeader>
                            {product.material && (
                                <p className="mt-3 inline-flex items-center gap-1.5 text-xs font-bold text-brand-forest">
                                    <Recycle className="w-3.5 h-3.5" strokeWidth={2.6} /> Bahan: {product.material}
                                </p>
                            )}
                            {product.creator_name && (
                                <div className="mt-3 rounded-2xl border-2 border-brand-ink/15 bg-brand-cream p-3">
                                    <Link
                                        to={`/kreator/${encodeURIComponent(product.creator_name)}`}
                                        data-testid={`product-creator-link-modal-${product.id}`}
                                        className="font-display font-bold text-sm hover:text-brand-forest transition-colors"
                                    >
                                        {product.creator_name}
                                    </Link>
                                    {product.creator_bio && <p className="text-xs text-brand-ink/60 mt-0.5 leading-relaxed">{product.creator_bio}</p>}
                                    {product.phone && (
                                        <p data-testid={`product-phone-${product.id}`} className="mt-1 inline-flex items-center gap-1 text-xs font-bold text-brand-forest">
                                            <MessageCircle className="w-3.5 h-3.5" strokeWidth={2.6} /> Kontak produk: {formatPhone(product.phone)}
                                        </p>
                                    )}
                                </div>
                            )}
                            <div className="mt-auto pt-5">
                                <p className="font-display text-3xl font-extrabold">{formatIDR(product.price)}</p>
                                <div className="mt-4 flex gap-2">
                                    <button
                                        data-testid={`product-share-button-modal-${product.id}`}
                                        onClick={() => setShareOpen(true)}
                                        aria-label="Bagikan karya"
                                        className="p-4 rounded-full border-2 border-brand-ink bg-white hover:bg-brand-sunshine/60 transition-colors"
                                    >
                                        <Share2 className="w-5 h-5" strokeWidth={2.4} />
                                    </button>
                                    {product.sold_out ? (
                                        <span
                                            data-testid={`product-soldout-label-modal-${product.id}`}
                                            className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-full border-2 border-brand-ink bg-brand-ink/15 text-brand-ink font-display font-bold cursor-not-allowed"
                                        >
                                            <PackageX className="w-5 h-5" strokeWidth={2.4} /> Stok Habis — Segera Restok
                                        </span>
                                    ) : (
                                        <a
                                            data-testid={`product-wa-order-button-modal-${product.id}`}
                                            href={waOrderLink(product.title, product.price, product.phone)}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-full border-2 border-brand-ink bg-brand-wa text-white font-display font-bold shadow-hard-sm hover:-translate-y-0.5 hover:shadow-hard transition-all active:translate-y-0 active:shadow-hard-sm"
                                        >
                                            <MessageCircle className="w-5 h-5" strokeWidth={2.4} /> Order via WhatsApp
                                        </a>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
            <ShareDialog product={product} open={shareOpen} onOpenChange={setShareOpen} />
        </>
    );
}
